import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PageTemplate from '../PageTemplate';
import './InvestmentAnalysis.css';

const InvestmentAnalysis = () => {
  const { t } = useTranslation();

  const breadcrumbs = [
    { labelKey: 'nav.services', href: '/#services' },
    { labelKey: 'services.investmentAnalysis' }
  ];

  // 设置页面标题
  useEffect(() => {
    document.title = `${t('pages.investmentAnalysis.title')} - ${t('common.companyName')}`;
  }, [t]);

  return (
    <PageTemplate 
      titleKey="pages.investmentAnalysis.title"
      subtitleKey="pages.investmentAnalysis.subtitle"
      breadcrumbs={breadcrumbs}
    >
      {/* CTA 按钮 */}
      <div className="investment-cta-top">
        <a href="/connect-us" className="cta-button-primary">
          {t('pages.investmentAnalysis.cta')}
        </a>
      </div>

      {/* 投资环境洞察：机遇与风险 */}
      <div className="landscape-section">
        <h2>{t('pages.investmentAnalysis.landscape.title')}</h2>
        <div className="landscape-grid">
          {/* 左侧：风险管控 */}
          <div className="risk-column">
            <h3>{t('pages.investmentAnalysis.landscape.riskTitle')}</h3>
            <div className="risk-list">
              {[1, 2, 3, 4].map((num) => (
                <div key={num} className="risk-item">
                  <h4>{t(`pages.investmentAnalysis.landscape.risk${num}.title`)}</h4>
                  <p>{t(`pages.investmentAnalysis.landscape.risk${num}.desc`)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* 右侧：市场机遇 */}
          <div className="opportunities-column">
            <h3>{t('pages.investmentAnalysis.landscape.opportunitiesTitle')}</h3>
            <div className="opportunity-list">
              {[1, 2, 3, 4].map((num) => (
                <div key={num} className="opportunity-item">
                  <h4>{t(`pages.investmentAnalysis.landscape.opportunity${num}.title`)}</h4>
                  <p>{t(`pages.investmentAnalysis.landscape.opportunity${num}.desc`)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 价值主张 */}
      <div className="value-proposition-section">
        <h2>{t('pages.investmentAnalysis.valueProposition.title')}</h2>
        <p className="value-statement">
          {t('pages.investmentAnalysis.valueProposition.statement')}
        </p>
      </div>

      {/* 专业分析服务 */}
      <div className="services-section">
        <h2>{t('pages.investmentAnalysis.services.title')}</h2>
        <div className="services-list">
          {[1, 2, 3, 4, 5, 6].map((num) => (
            <div key={num} className="service-item">
              <h3>{t(`pages.investmentAnalysis.services.item${num}.title`)}</h3>
              <p>{t(`pages.investmentAnalysis.services.item${num}.desc`)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* 四维分析框架 */}
      <div className="framework-section">
        <h2>{t('pages.investmentAnalysis.framework.title')}</h2>
        <div className="framework-grid">
          {[1, 2, 3, 4].map((num) => (
            <div key={num} className="framework-card">
              <h3>{t(`pages.investmentAnalysis.framework.dimension${num}.title`)}</h3>
              <p>{t(`pages.investmentAnalysis.framework.dimension${num}.desc`)}</p>
            </div>
          ))}
        </div>
        <p className="framework-summary">
          {t('pages.investmentAnalysis.framework.summary')}
        </p>
      </div>

      {/* 专业能力展示 */}
      <div className="capabilities-section">
        <h2>{t('pages.investmentAnalysis.capabilities.title')}</h2>
        
        {/* 数据指标 */}
        <div className="stats-grid-modern">
          <div className="stat-card">
            <div className="stat-number">
              {t('pages.investmentAnalysis.capabilities.stats.amount.number')}
            </div>
            <div className="stat-label">
              {t('pages.investmentAnalysis.capabilities.stats.amount.label')}
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-number">
              {t('pages.investmentAnalysis.capabilities.stats.projects.number')}
            </div>
            <div className="stat-label">
              {t('pages.investmentAnalysis.capabilities.stats.projects.label')}
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-number">
              {t('pages.investmentAnalysis.capabilities.stats.roi.number')}
            </div>
            <div className="stat-label">
              {t('pages.investmentAnalysis.capabilities.stats.roi.label')}
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-number">
              {t('pages.investmentAnalysis.capabilities.stats.industries.number')}
            </div>
            <div className="stat-label">
              {t('pages.investmentAnalysis.capabilities.stats.industries.label')}
            </div>
          </div>
        </div>

        {/* 核心优势 */}
        <div className="advantages-content">
          <h3>{t('pages.investmentAnalysis.capabilities.advantages.title')}</h3>
          <div className="advantages-grid">
            {[1, 2, 3, 4].map((num) => (
              <div key={num} className="advantage-card">
                <h4>{t(`pages.investmentAnalysis.capabilities.advantages.item${num}.title`)}</h4>
                <p>{t(`pages.investmentAnalysis.capabilities.advantages.item${num}.desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 分析流程 */}
      <div className="process-section">
        <h2>{t('pages.investmentAnalysis.process.title')}</h2>
        <div className="process-timeline">
          {[1, 2, 3, 4].map((num) => (
            <div key={num} className="process-step">
              <div className="step-content">
                <h3>{t(`pages.investmentAnalysis.process.step${num}.title`)}</h3>
                <p>{t(`pages.investmentAnalysis.process.step${num}.desc`)}</p>
              </div>
              {num < 4 && <div className="step-arrow">→</div>}
            </div>
          ))}
        </div>
        <p className="process-quality">
          {t('pages.investmentAnalysis.process.quality')}
        </p>
      </div>

      {/* 最终行动号召 */}
      <div className="final-cta-section">
        <h2>{t('pages.investmentAnalysis.callToAction.title')}</h2>
        <p>{t('pages.investmentAnalysis.callToAction.description')}</p>
        <a href="/connect-us" className="cta-button-large">
          {t('pages.investmentAnalysis.callToAction.button')}
        </a>
      </div>
    </PageTemplate>
  );
};

export default InvestmentAnalysis;
